import { useRef, useState } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { cn } from "@/utils/cn";
import { EXPORT_FORMAT_META, exportData, type ExportColumn, type ExportFormat } from "@/utils/export";

interface ExportMenuProps<T> {
  /** Données réellement affichées (recherches, filtres et périodes déjà appliqués). */
  rows: T[];
  columns: ExportColumn<T>[];
  filename: string;
  title?: string;
  subtitle?: string;
  /** Formats proposés. PDF peut être retiré si le contenu ne s'y prête pas. */
  formats?: ExportFormat[];
  label?: string;
  className?: string;
}

export default function ExportMenu<T>({
  rows,
  columns,
  filename,
  title,
  subtitle,
  formats = ["xlsx", "csv", "pdf"],
  label = "Exporter",
  className,
}: ExportMenuProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  useClickOutside(containerRef, () => setOpen(false), open);

  const isEmpty = rows.length === 0 || columns.length === 0;

  const handleExport = async (format: ExportFormat) => {
    if (isEmpty || busy) return;
    setError(null);
    setBusy(format);
    try {
      await exportData(format, rows, columns, { filename, title, subtitle });
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'export a échoué.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={isEmpty}
        title={isEmpty ? "Aucune donnée à exporter." : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3.5 py-2.5 text-sm font-label font-medium transition-colors",
          isEmpty
            ? "cursor-not-allowed border-background-200 bg-background-100 text-foreground-400"
            : "cursor-pointer border-foreground-200 bg-background-50 text-foreground-900 hover:bg-background-100"
        )}
      >
        <i className="ri-download-2-line text-base" aria-hidden="true" />
        {label}
        <i className={cn("ri-arrow-down-s-line text-base transition-transform", open && "rotate-180")} aria-hidden="true" />
      </button>

      {open && !isEmpty && (
        <div
          role="menu"
          className="absolute left-0 right-auto z-30 mt-1.5 w-52 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-background-200 bg-background-50 py-1 shadow-sm animate-fade-in sm:left-auto sm:right-0"
        >
          {formats.map((format) => {
            const meta = EXPORT_FORMAT_META[format];
            return (
              <button
                key={format}
                type="button"
                role="menuitem"
                onClick={() => handleExport(format)}
                disabled={Boolean(busy)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-60 cursor-pointer"
              >
                <i
                  className={cn(busy === format ? "ri-loader-4-line animate-spin" : meta.icon, "text-base text-foreground-500")}
                  aria-hidden="true"
                />
                <span className="flex-1">{meta.label}</span>
                <span className="text-xs text-foreground-400">{rows.length}</span>
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <p className="absolute left-0 right-auto top-full mt-1.5 w-56 max-w-[calc(100vw-2rem)] rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800 sm:left-auto sm:right-0">
          {error}
        </p>
      )}
    </div>
  );
}